import { timestampToDate } from "@storytell/cp-client";
import dayjs from "dayjs";
import type { TranslationKey } from "~/i18n/translations";

type TFn = (key: TranslationKey, params?: Record<string, string>) => string;

/** Last activity timestamp (supports camelCase and snake_case from API) */
export const lastActivityMs = (chat: {
  createdAt?: { seconds?: bigint; nanos?: number };
  modifiedAt?: { seconds?: bigint; nanos?: number };
  created_at?: { seconds?: bigint; nanos?: number };
  modified_at?: { seconds?: bigint; nanos?: number };
}) => {
  const ts =
    chat.modifiedAt ??
    (chat as { modified_at?: { seconds?: bigint; nanos?: number } }).modified_at ??
    chat.createdAt ??
    (chat as { created_at?: { seconds?: bigint; nanos?: number } }).created_at;
  if (!ts) return 0;
  return Number(ts.seconds ?? 0) * 1000 + Math.floor((ts.nanos ?? 0) / 1e6);
};

/** Timestamp to show for "last activity" (supports snake_case from API) */
export const getDisplayTimestamp = (chat: {
  createdAt?: Parameters<typeof timestampToDate>[0];
  modifiedAt?: Parameters<typeof timestampToDate>[0];
  created_at?: Parameters<typeof timestampToDate>[0];
  modified_at?: Parameters<typeof timestampToDate>[0];
}) =>
  chat.modifiedAt ??
  (chat as { modified_at?: Parameters<typeof timestampToDate>[0] }).modified_at ??
  chat.createdAt ??
  (chat as { created_at?: Parameters<typeof timestampToDate>[0] }).created_at;

/** Created timestamp (for "First created" line) */
const getCreatedTimestamp = (chat: {
  createdAt?: Parameters<typeof timestampToDate>[0];
  created_at?: Parameters<typeof timestampToDate>[0];
}) => chat.createdAt ?? (chat as { created_at?: Parameters<typeof timestampToDate>[0] }).created_at;

const getAttributionLabel = (
  userId: string | undefined,
  userDisplayName: string | undefined,
  userEmail: string | undefined,
  currentUserId: string | undefined,
  t: TFn
): string => {
  if (userId && currentUserId === userId) return t("common.you");
  return userDisplayName?.trim() || userEmail?.trim() || t("label_someone_else");
};

type AuditLike = {
  userId?: string;
  userDisplayName?: string;
  userEmail?: string;
  user_id?: string;
  user_display_name?: string;
  user_email?: string;
};
type AuditMetaLike = { created?: AuditLike; modified?: AuditLike };

const calendarDayDiff = (date: Date): number => dayjs().startOf("day").diff(dayjs(date).startOf("day"), "day");

/** Date string for "Created by X on Y" / "Last chat by X on Y": no "on" for Today/Yesterday/N days ago, "on" for calendar dates. */
const formatDateForAttribution = (timestamp: Parameters<typeof timestampToDate>[0], t: TFn): string => {
  // Skip during SSR: the Node runtime's default TZ (UTC in prod) would bake a UTC wall-clock time
  // into the HTML, and Solid hydration may not replace it. Client-only render guarantees the user's
  // local TZ via toLocaleX defaults.
  if (typeof window === "undefined") return "";
  const date = timestampToDate(timestamp);
  const diffDays = calendarDayDiff(date);
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  if (diffDays === 0) return t("time_today_at", { time: timeStr });
  if (diffDays === 1) return t("time_yesterday_at", { time: timeStr });
  if (diffDays < 7) return t("time_days_ago_at", { count: diffDays.toString(), time: timeStr });
  const absolute = date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  return `on ${absolute}`;
};

/** Created/modified attribution and dates for a chat (supports camelCase and snake_case from API) */
export const getChatAuditLines = (
  chat: {
    createdAt?: Parameters<typeof timestampToDate>[0];
    created_at?: Parameters<typeof timestampToDate>[0];
    modifiedAt?: Parameters<typeof timestampToDate>[0];
    modified_at?: Parameters<typeof timestampToDate>[0];
  } & { auditMetadata?: AuditMetaLike; audit_metadata?: AuditMetaLike },
  t: TFn,
  currentUserId: string | undefined
) => {
  const meta = chat.auditMetadata ?? chat.audit_metadata;
  const created = meta?.created;
  const modified = meta?.modified;
  const createdBy = getAttributionLabel(
    created?.userId ?? created?.user_id,
    created?.userDisplayName ?? created?.user_display_name,
    created?.userEmail ?? created?.user_email,
    currentUserId,
    t
  );
  const modifiedBy = getAttributionLabel(
    modified?.userId ?? modified?.user_id,
    modified?.userDisplayName ?? modified?.user_display_name,
    modified?.userEmail ?? modified?.user_email,
    currentUserId,
    t
  );
  const createdTs = getCreatedTimestamp(chat);
  const modifiedTs = getDisplayTimestamp(chat);
  return {
    createdBy,
    modifiedBy,
    createdDate: createdTs ? formatDateForAttribution(createdTs, t) : null,
    modifiedDate: modifiedTs ? formatDateForAttribution(modifiedTs, t) : null,
  };
};

/**
 * Whether a chat belongs to the current user — i.e., they created it OR last modified it.
 * Shared by the sidebar ("My Recent Chats") and the Chats page "Mine" filter so the two surfaces
 * agree. Matches by userId first, then falls back to a case-insensitive email match — mirroring
 * `getAttributionLabel` so a chat that displays "Created by You" in the hover detail also appears
 * under the "Mine" filter. Returns false when both identifiers are unknown (auth still loading)
 * to avoid leaking chats. Supports camelCase and snake_case audit metadata variants.
 */
export const isChatMine = (
  chat: { auditMetadata?: AuditMetaLike; audit_metadata?: AuditMetaLike },
  currentUserId: string | undefined,
  currentUserEmail: string | undefined
): boolean => {
  if (!currentUserId && !currentUserEmail) return false;
  const normalizedEmail = currentUserEmail?.toLowerCase();
  const matches = (audit: AuditLike | undefined): boolean => {
    if (!audit) return false;
    const auditUserId = audit.userId ?? audit.user_id;
    if (currentUserId && auditUserId && auditUserId === currentUserId) return true;
    const auditEmail = (audit.userEmail ?? audit.user_email)?.toLowerCase();
    if (normalizedEmail && auditEmail && auditEmail === normalizedEmail) return true;
    return false;
  };
  const meta = chat.auditMetadata ?? chat.audit_metadata;
  return matches(meta?.created) || matches(meta?.modified);
};

/**
 * Elapsed-time buckets from `date` until now — the shared arithmetic behind the relative formatters
 * below. dayjs computes each unit so the month/year buckets follow the calendar (variable month
 * lengths, leap years, DST) instead of fixed 30-/365-day approximations.
 */
const relativeUnits = (date: Date) => {
  const now = dayjs();
  const target = dayjs(date);
  return {
    seconds: now.diff(target, "second"),
    minutes: now.diff(target, "minute"),
    hours: now.diff(target, "hour"),
    days: now.diff(target, "day"),
    weeks: now.diff(target, "week"),
    months: now.diff(target, "month"),
    years: now.diff(target, "year"),
  };
};

/**
 * Verbose, localized relative time — `5 seconds ago`, `2 hours ago`, `3 days ago`. Backed by
 * Intl.RelativeTimeFormat so pluralization and locale come for free (no per-string i18n keys).
 * Shared home for the chat prompt timeline's row labels; client-only so it renders against the
 * viewer's locale rather than the SSR default.
 */
export const formatRelativeTimeLong = (date: Date): string => {
  if (typeof window === "undefined") return "";
  const { seconds, minutes, hours, days, months, years } = relativeUnits(date);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "always", style: "long" });
  if (seconds < 60) return rtf.format(-seconds, "second");
  if (minutes < 60) return rtf.format(-minutes, "minute");
  if (hours < 24) return rtf.format(-hours, "hour");
  if (days < 30) return rtf.format(-days, "day");
  if (months < 12) return rtf.format(-months, "month");
  return rtf.format(-years, "year");
};

/**
 * Absolute date for the expanded sidebar column, where the extra width earns precision:
 * today → `2:16 PM`, earlier this year → `Jul 7`, prior years → `Jun 5, 2025`. Locale formatting keeps
 * month names and time translated without per-string i18n keys. Client-only for the local-TZ reason above.
 */
export const formatChatTimestampDate = (timestamp: Parameters<typeof timestampToDate>[0]): string => {
  if (typeof window === "undefined") return "";
  const date = timestampToDate(timestamp);
  const now = dayjs();
  if (now.isSame(date, "day")) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  }
  const opts: Intl.DateTimeFormatOptions = now.isSame(date, "year")
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString(undefined, opts);
};

/** Local-day buckets for the sidebar's recent-chat grouping. */
export type ChatDateGroup = "today" | "yesterday" | "previous7" | "older";

/**
 * Which bucket a chat falls in, by calendar day rather than elapsed hours — 11pm
 * last night is "yesterday" at 9am today. Future timestamps (clock skew) read as today.
 */
export const getChatDateGroup = (timestamp: Parameters<typeof timestampToDate>[0]): ChatDateGroup => {
  const diffDays = calendarDayDiff(timestampToDate(timestamp));
  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return "previous7";
  return "older";
};

/** Time of day alone, for rows whose date is already carried by a group heading. */
export const formatChatTimeOfDay = (timestamp: Parameters<typeof timestampToDate>[0]): string => {
  if (typeof window === "undefined") return "";
  return timestampToDate(timestamp).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/** Relative date + time for list row (matches Chats page row). */
export const formatChatListRowDate = (timestamp: Parameters<typeof timestampToDate>[0], t: TFn): string => {
  // See formatDateForAttribution above — render only on the client so the user's local TZ is used.
  if (typeof window === "undefined") return "";
  const date = timestampToDate(timestamp);
  const diffDays = calendarDayDiff(date);
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });

  if (diffDays === 0) {
    return t("time_today_at", { time: timeStr });
  }
  if (diffDays === 1) {
    return t("time_yesterday_at", { time: timeStr });
  }
  if (diffDays < 7) {
    return t("time_days_ago_at", { count: diffDays.toString(), time: timeStr });
  }

  return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
};
